import { mergeAttachmentFlagsCache } from "@/lib/cache/attachmentFlagsCache";
import {
  bodyStoreId,
  idbClearAccount,
  idbClearAll,
  idbGetBody,
  idbPrimeBodies,
  idbPutBody,
  idbPutMany,
  idbRelocateBody,
  idbDeleteBody,
} from "@/lib/cache/messageBodyIndexedDB";
import type { MailMessageDetail } from "@/lib/types/mail";
import { decodeMimeWords } from "@/lib/mail/mimeHeaderDecode";
import {
  clearMailBodyDocumentCache,
  clearMailBodyDocumentCacheForAccount,
} from "@/lib/message/mailBodyDocumentCache";

const MAX_MEMORY_ENTRIES = 250;
const LEGACY_SESSION_KEY = "mail_body_cache_v1";
const LEGACY_SESSION_MAX = 40;

const cache = new Map<string, MailMessageDetail>();
/** Corps déjà lus / primés — survit à l’éviction LRU mémoire (pastille verte stable). */
const knownReadyKeys = new Set<string>();
const listeners = new Set<() => void>();
let legacyMigrationStarted = false;

type StoredBody = MailMessageDetail & { accountId: number; folder: string };

function cacheKey(accountId: number, folder: string, uid: number): string {
  return bodyStoreId(accountId, folder, uid);
}

export function sanitizeMailMessageDetail(message: MailMessageDetail): MailMessageDetail {
  const subject = decodeMimeWords(message.subject, { collapseSpaces: true });
  const from = decodeMimeWords(message.from, { collapseSpaces: true });
  const to = decodeMimeWords(message.to, { collapseSpaces: true });
  const cc = message.cc
    ? decodeMimeWords(message.cc, { collapseSpaces: true })
    : message.cc;
  const bodyText = message.bodyText?.includes("=?")
    ? decodeMimeWords(message.bodyText)
    : message.bodyText;
  const bodyHtml = message.bodyHtml?.includes("=?")
    ? decodeMimeWords(message.bodyHtml)
    : message.bodyHtml;

  return {
    ...message,
    subject: subject || message.subject,
    from: from || message.from,
    to: to || message.to,
    cc: cc || message.cc,
    bodyText: bodyText || message.bodyText,
    bodyHtml: bodyHtml ?? message.bodyHtml,
  };
}

function promoteToMemory(
  accountId: number,
  folder: string,
  message: MailMessageDetail,
  notify = true
): void {
  const key = cacheKey(accountId, folder, message.uid);
  if (cache.size >= MAX_MEMORY_ENTRIES && !cache.has(key)) {
    const oldest = cache.keys().next().value;
    if (oldest) cache.delete(oldest);
  }
  const sanitized = sanitizeMailMessageDetail(message);
  cache.set(key, sanitized);
  if (
    sanitized.uid > 0 &&
    (Boolean(sanitized.bodyHtml?.trim() || sanitized.bodyText?.trim()) ||
      sanitized.bodyEmpty === true ||
      sanitized.emlStored === true)
  ) {
    knownReadyKeys.add(key);
  }
  if (notify) notifyMessageBodyCache();
}

/** True si le corps a déjà été hydraté client (mémoire ou IDB), même après éviction LRU. */
export function isMessageBodyKnownReady(
  accountId: number,
  folder: string,
  uid: number
): boolean {
  if (uid <= 0) return false;
  return knownReadyKeys.has(cacheKey(accountId, folder, uid));
}

function hydrateFromLegacySession(): void {
  try {
    const raw = sessionStorage.getItem(LEGACY_SESSION_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as StoredBody[];
    if (!Array.isArray(parsed)) return;
    for (const entry of parsed.slice(-LEGACY_SESSION_MAX)) {
      if (!entry?.uid || entry.uid < 1 || !entry.accountId || !entry.folder) continue;
      const { accountId, folder, ...message } = entry;
      promoteToMemory(accountId, folder, message, false);
    }
  } catch {
    /* ignore */
  }
}

function migrateLegacySessionToIndexedDB(): void {
  if (legacyMigrationStarted) return;
  legacyMigrationStarted = true;

  void (async () => {
    try {
      const raw = sessionStorage.getItem(LEGACY_SESSION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as StoredBody[];
      if (!Array.isArray(parsed)) return;

      const batch = parsed
        .slice(-LEGACY_SESSION_MAX)
        .filter((entry) => entry?.uid > 0 && entry.accountId > 0 && entry.folder)
        .map((entry) => {
          const { accountId, folder, ...message } = entry;
          return { accountId, folder, message };
        });

      if (batch.length > 0) {
        await idbPutMany(batch);
        notifyMessageBodyCache();
      }
      sessionStorage.removeItem(LEGACY_SESSION_KEY);
    } catch {
      /* ignore */
    }
  })();
}

hydrateFromLegacySession();
migrateLegacySessionToIndexedDB();

export function subscribeMessageBodyCache(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyMessageBodyCache(): void {
  listeners.forEach((listener) => listener());
}

/** Lecture synchrone — mémoire uniquement (hot path UI déjà primée). */
export function readMessageBodyCache(
  accountId: number,
  folder: string,
  uid: number
): MailMessageDetail | null {
  return cache.get(cacheKey(accountId, folder, uid)) ?? null;
}

/** Lecture async — mémoire puis IndexedDB (0 requête réseau si hit). */
export async function readMessageBodyCacheAsync(
  accountId: number,
  folder: string,
  uid: number
): Promise<MailMessageDetail | null> {
  const inMemory = readMessageBodyCache(accountId, folder, uid);
  if (inMemory) return inMemory;

  const fromIdb = await idbGetBody(accountId, folder, uid);
  if (!fromIdb) return null;

  promoteToMemory(accountId, folder, fromIdb);
  return readMessageBodyCache(accountId, folder, uid);
}

/** Précharge les corps visibles depuis IndexedDB (pastilles vertes après reload). */
export async function primeMessageBodyCacheFromIndexedDB(
  accountId: number,
  folder: string,
  uids: number[],
  priorityUids?: ReadonlySet<number>
): Promise<void> {
  const missing = uids.filter(
    (uid) => uid > 0 && readMessageBodyCache(accountId, folder, uid) == null
  );
  if (missing.length === 0) return;

  const ordered =
    priorityUids && priorityUids.size > 0
      ? [
          ...missing.filter((uid) => priorityUids.has(uid)),
          ...missing.filter((uid) => !priorityUids.has(uid)),
        ]
      : missing;

  const hits = await idbPrimeBodies(accountId, folder, ordered);
  if (hits.length === 0) return;

  const priorityHits: typeof hits = [];
  const otherHits: typeof hits = [];
  for (const record of hits) {
    if (priorityUids?.has(record.uid)) priorityHits.push(record);
    else otherHits.push(record);
  }

  /* LRU Map : promouvoir les non-lus en dernier pour qu’ils restent en mémoire. */
  for (const record of otherHits) {
    promoteToMemory(record.accountId, record.folder, record.message, false);
  }
  for (const record of priorityHits) {
    promoteToMemory(record.accountId, record.folder, record.message, false);
  }
  notifyMessageBodyCache();
}

export function writeMessageBodyCache(
  accountId: number,
  folder: string,
  message: MailMessageDetail
): void {
  const key = cacheKey(accountId, folder, message.uid);
  const existing = cache.get(key);
  const withMeta =
    (message.attachments?.length ?? 0) > 0 || !existing
      ? message
      : {
          ...message,
          attachments: existing.attachments,
          hasAttachments:
            message.hasAttachments === true || (existing.attachments?.length ?? 0) > 0,
        };
  const sanitized = sanitizeMailMessageDetail(withMeta);
  promoteToMemory(accountId, folder, sanitized);

  if (sanitized.uid > 0 && sanitized.hasAttachments !== undefined) {
    mergeAttachmentFlagsCache(
      accountId,
      folder,
      new Map([[sanitized.uid, sanitized.hasAttachments === true]])
    );
  }

  void idbPutBody(accountId, folder, sanitized).catch(() => {
    /* quota / mode privé */
  });
}

/** Après move IMAP : déplace la copie corps en mémoire + IndexedDB. */
export function relocateMessageBodyCache(
  accountId: number,
  uid: number,
  fromFolder: string,
  toFolder: string
): void {
  if (uid <= 0 || fromFolder === toFolder) return;

  const oldKey = cacheKey(accountId, fromFolder, uid);
  const message = cache.get(oldKey);
  const wasReady = knownReadyKeys.has(oldKey);
  if (message) {
    cache.delete(oldKey);
    cache.set(cacheKey(accountId, toFolder, uid), message);
  }
  if (wasReady) {
    knownReadyKeys.delete(oldKey);
    knownReadyKeys.add(cacheKey(accountId, toFolder, uid));
  }
  if (message || wasReady) {
    notifyMessageBodyCache();
  }

  void idbRelocateBody(accountId, uid, fromFolder, toFolder).catch(() => {
    /* ignore */
  });
}

/** Retire un corps du cache client (purge stockage local). */
export function removeMessageBodyCacheEntry(
  accountId: number,
  folder: string,
  uid: number
): void {
  if (uid <= 0 || accountId <= 0) return;
  const key = cacheKey(accountId, folder, uid);
  cache.delete(key);
  knownReadyKeys.delete(key);
  void idbDeleteBody(accountId, folder, uid).catch(() => {
    /* ignore */
  });
  notifyMessageBodyCache();
}

export function clearMessageBodyCacheForAccount(accountId: number): void {
  const prefix = `${accountId}\0`;
  for (const key of [...cache.keys()]) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
  for (const key of [...knownReadyKeys]) {
    if (key.startsWith(prefix)) {
      knownReadyKeys.delete(key);
    }
  }
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    /* ignore */
  }
  void idbClearAccount(accountId).catch(() => {
    /* ignore */
  });
  clearMailBodyDocumentCacheForAccount(accountId);
  notifyMessageBodyCache();
}

export function clearAllMessageBodyCaches(): void {
  cache.clear();
  knownReadyKeys.clear();
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    /* ignore */
  }
  void idbClearAll().catch(() => {
    /* ignore */
  });
  clearMailBodyDocumentCache();
  notifyMessageBodyCache();
}
